import { useEffect } from 'react';

/**
 * Custom hook to handle scroll position tracking and restoration
 * Tracks user scroll position and last edited component during interactions
 * Restores scroll position and table visibility after save operations
 * 
 * @param {number} tabValue - Current tab value to track component edits per tab
 */
export function useScrollPositionTracking(tabValue) {
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = {
        x: window.scrollX || window.pageXOffset,
        y: window.scrollY || window.pageYOffset,
        timestamp: Date.now(),
      };
      sessionStorage.setItem('soe_scroll_position', JSON.stringify(scrollPosition));
    };

    // Track scroll position within table containers (for sticky headers)
    const handleTableScroll = (event) => {
      const tableElement = event.target;
      // Check if target is an element and has getAttribute method
      if (tableElement && tableElement.nodeType === 1 && typeof tableElement.getAttribute === 'function') {
        if (tableElement.getAttribute('data-table-container')) {
          const tableName = tableElement.getAttribute('data-table-name');
          if (tableName) {
            const scrollInfo = {
              scrollTop: tableElement.scrollTop,
              scrollLeft: tableElement.scrollLeft,
              tableName,
              timestamp: Date.now(),
            };
            sessionStorage.setItem(`soe_table_scroll_${tableName}`, JSON.stringify(scrollInfo));
          }
        }
      }
    };

    const detectComponentFromTarget = (target) => {
      // Search up the DOM tree until finding a data-table-name
      let element = target;
      let maxDepth = 20; // Search depth limit
      
      while (element && element !== document.body && maxDepth > 0) {
        const tableName = element.getAttribute?.('data-table-name');
        if (tableName) {
          return tableName;
        }
        
        element = element.parentElement;
        maxDepth--;
      }

      return null;
    };

    const saveEditedComponent = (componentName, eventType) => {
      if (componentName) {
        const editInfo = {
          componentName,
          timestamp: Date.now(),
          tabValue,
          eventType, // For debugging
        };
        sessionStorage.setItem('soe_last_edited_component', JSON.stringify(editInfo));
      }
    };

    const handleInteraction = (event) => {
      const target = event.target;
      
      // Check if it's an editable element (input, textarea, select, contenteditable)
      const isEditableElement = 
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable ||
        target.closest('input, textarea, select, [contenteditable="true"]');
      
      // Check if it's a button or click on interactive element (for delete, add, etc.)
      const isButtonClick = 
        event.type === 'click' && (
          target.tagName === 'BUTTON' ||
          target.closest('button') ||
          target.closest('[role="button"]') ||
          // Material-UI IconButton
          target.closest('.MuiIconButton-root') ||
          // SVG icons inside buttons
          target.closest('svg')?.closest('button')
        );
      
      if (isEditableElement || isButtonClick) {
        const componentName = detectComponentFromTarget(target);
        if (componentName) {
          saveEditedComponent(componentName, event.type);
        }
      }
    };

    // Add listeners for multiple events
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleTableScroll, true); // Capture phase for child scrolls
    document.addEventListener('focusin', handleInteraction, true);
    document.addEventListener('click', handleInteraction, true);
    document.addEventListener('input', handleInteraction, true);
    document.addEventListener('change', handleInteraction, true);

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleTableScroll, true);
      document.removeEventListener('focusin', handleInteraction, true);
      document.removeEventListener('click', handleInteraction, true);
      document.removeEventListener('input', handleInteraction, true);
      document.removeEventListener('change', handleInteraction, true);
    };
  }, [tabValue]);
}

/**
 * Saves current scroll position and edited component info to sessionStorage
 * Should be called before save operation
 * Captures state DIRECTLY from DOM (not from tracking) to avoid inconsistencies
 */
export function saveScrollStateBeforeSave() {
  // 1. Save current page scroll position
  const scrollPosition = {
    x: window.scrollX || window.pageXOffset,
    y: window.scrollY || window.pageYOffset,
    timestamp: Date.now(),
  };
  sessionStorage.setItem('soe_scroll_position_before_save', JSON.stringify(scrollPosition));
  
  // 2. Get the last edited component from continuous tracking
  const lastEditedComponent = sessionStorage.getItem('soe_last_edited_component');
  
  if (lastEditedComponent) {
    try {
      const { componentName } = JSON.parse(lastEditedComponent);
      
      // 3. Find the table element in the DOM
      const tableElement = document.querySelector(`[data-table-name="${componentName}"]`);
      if (!tableElement) {
        return;
      }
      
      // 4. Save component name for restoration
      sessionStorage.setItem('soe_last_edited_component_before_save', componentName);
      
      // 5. Find and save table container scroll position (if it exists)
      const tableContainer = tableElement.querySelector('[data-table-container="true"]');
      
      if (tableContainer) {
        const tableScrollInfo = {
          scrollTop: tableContainer.scrollTop,
          scrollLeft: tableContainer.scrollLeft,
        };
        sessionStorage.setItem(`soe_table_scroll_before_save_${componentName}`, JSON.stringify(tableScrollInfo));
      }
    } catch (err) {
      console.error('Error saving scroll state:', err);
    }
  }
}

/**
 * Parses and retrieves saved page scroll position
 * @returns {Object|null} { x, y } or null if not found
 */
function getSavedPageScroll() {
  const savedScroll = sessionStorage.getItem('soe_scroll_position_before_save');
  if (!savedScroll) return null;
  
  try {
    const { x, y } = JSON.parse(savedScroll);
    return { x, y };
  } catch (err) {
    console.error('Error parsing saved scroll:', err);
    return null;
  }
}

/**
 * Parses and retrieves saved table scroll position
 * @returns {Object|null} { componentName, scrollTop, scrollLeft } or null if not found
 */
function getSavedTableScroll() {
  const componentName = sessionStorage.getItem('soe_last_edited_component_before_save');
  if (!componentName) return null;
  
  try {
    const tableScrollInfo = sessionStorage.getItem(`soe_table_scroll_before_save_${componentName}`);
    
    if (tableScrollInfo) {
      const { scrollTop, scrollLeft } = JSON.parse(tableScrollInfo);
      return { componentName, scrollTop, scrollLeft };
    }
  } catch (err) {
    console.error('Error parsing saved table scroll:', err);
  }
  
  return null;
}

/**
 * Dispatches visibility event to open accordion for specific table
 * @param {string} componentName - Name of the component/table to make visible
 */
function dispatchVisibilityEvent(componentName) {
  window.dispatchEvent(new CustomEvent('soe-restore-visibility', {
    detail: { componentName }
  }));
}

/**
 * Restores page scroll position
 * @param {number} targetY - Target Y scroll position
 * @param {number} targetX - Target X scroll position
 */
function restorePageScroll(targetY, targetX) {
  window.scrollTo({ 
    top: targetY, 
    left: targetX, 
    behavior: 'instant' 
  });
}

/**
 * Restores table scroll position
 * Returns true if successful, false otherwise
 * @param {string} componentName - Name of the component/table
 * @param {number} scrollTop - Target scroll top position
 * @param {number} scrollLeft - Target scroll left position
 * @returns {boolean} True if restoration was successful
 */
function restoreTableScroll(componentName, scrollTop, scrollLeft) {
  if (scrollTop === 0 && scrollLeft === 0) {
    return true; // No scroll to restore
  }

  const tableContainer = findTableContainer(componentName);
  
  if (!tableContainer) {
    return false;
  }
  
  tableContainer.scrollTo({
    top: scrollTop,
    left: scrollLeft,
    behavior: 'instant'
  });
  
  // Verify scroll was applied
  const actualScrollTop = tableContainer.scrollTop;
  const actualScrollLeft = tableContainer.scrollLeft;
  
  if (Math.abs(actualScrollTop - scrollTop) > 5 || Math.abs(actualScrollLeft - scrollLeft) > 5) {
    return false;
  }
  
  return true;
}

/**
 * Finds and returns table container element
 * Opens accordion if container not found
 * @param {string} componentName - Name of the component/table
 * @returns {HTMLElement|null} Table container element or null
 */
function findTableContainer(componentName) {
  const tableElement = document.querySelector(`[data-table-name="${componentName}"]`);
  if (!tableElement) {
    return null;
  }
  
  let container = tableElement.querySelector('[data-table-container="true"]');
  
  // If container not found, accordion is closed - open it
  if (!container) {
    dispatchVisibilityEvent(componentName);
    
    // Try to find it again after dispatch (accordion might open synchronously)
    container = tableElement.querySelector('[data-table-container="true"]');
  }
  
  return container;
}

/**
 * Cleans up sessionStorage after restoration
 */
function cleanupScrollData() {
  // Get component name before cleaning up (it's now stored as a simple string)
  const componentName = sessionStorage.getItem('soe_last_edited_component_before_save');
  
  // Clean up all scroll restoration data
  sessionStorage.removeItem('soe_scroll_position');
  sessionStorage.removeItem('soe_last_edited_component');
  sessionStorage.removeItem('soe_scroll_position_before_save');
  sessionStorage.removeItem('soe_last_edited_component_before_save');
  
  // Clean up table scroll if component name exists
  if (componentName) {
    sessionStorage.removeItem(`soe_table_scroll_before_save_${componentName}`);
  }
}

/**
 * Waits for DOM to be ready before executing callback
 * Uses polling with timeout to ensure elements are rendered
 * @param {Function} callback - Function to execute when DOM is ready
 * @param {number} timeout - Maximum time to wait in milliseconds
 */
function waitForDOMReady(callback, timeout = 3000) {
  const startTime = Date.now();
  const pollInterval = 50; // Check every 50ms
  
  const poll = () => {
    const elapsed = Date.now() - startTime;
    
    // Timeout check
    if (elapsed > timeout) {
      callback(); // Try anyway
      return;
    }
    
    // Check if we need to wait for a table
    const componentName = sessionStorage.getItem('soe_last_edited_component_before_save');
    
    if (componentName) {
      const tableElement = document.querySelector(`[data-table-name="${componentName}"]`);
      const tableContainer = tableElement?.querySelector('[data-table-container="true"]');
      
      // Wait until table container is found and has dimensions
      if (tableContainer && tableContainer.offsetHeight > 0) {
        callback();
        return;
      }
    } else {
      // No table to wait for, just wait a bit for page render
      if (elapsed > 100) {
        callback();
        return;
      }
    }
    
    // Keep polling
    setTimeout(poll, pollInterval);
  };
  
  poll();
}

/**
 * Restores scroll position after save operation
 * Called after all data has been refreshed and re-renders are complete
 * Restores BOTH page scroll and table scroll (if applicable)
 * Uses polling to wait for DOM to be ready before restoring scroll
 */
export function restoreScrollAfterSave() {
  // Get saved scroll positions
  const savedPageScroll = getSavedPageScroll();
  const savedTableScroll = getSavedTableScroll();
  
  if (!savedPageScroll && !savedTableScroll) {
    return; // Nothing to restore
  }
  
  // 1. Open accordion first (if needed)
  if (savedTableScroll) {
    dispatchVisibilityEvent(savedTableScroll.componentName);
  }
  
  // 2. Wait for DOM to be ready, then restore scrolls
  waitForDOMReady(() => {
    // Restore table scroll first
    if (savedTableScroll) {
      restoreTableScroll(
        savedTableScroll.componentName,
        savedTableScroll.scrollTop,
        savedTableScroll.scrollLeft
      );
    }
    
    // Then restore page scroll
    if (savedPageScroll) {
      restorePageScroll(savedPageScroll.y, savedPageScroll.x);
    }
    
    // Cleanup after restoration
    cleanupScrollData();
  });
}